import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../lib/api";
import { Section } from "../components/Common";
import { toast } from "sonner";
import { Send, Users, Brain, Heart, Building2, Apple, Activity } from "lucide-react";
import { errorMessage } from "../lib/errors";

export default function WorkshopsPage() {
  const { t, i18n } = useTranslation();
  const services = t("workshops.services", { returnObjects: true });
  const [form, setForm] = useState({ name: "", email: "", topic: t("workshops.topic_default"), message: "" });
  const [sending, setSending] = useState(false);
  const icons = [Brain, Apple, Heart, Users, Activity, Building2];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("common.required_fields"));
      return;
    }
    setSending(true);
    try {
      await API.post("/support/contact", { ...form, language: i18n.language });
      toast.success(t("support.submitted"));
      setForm({ name: "", email: "", topic: t("workshops.topic_default"), message: "" });
    } catch (e) {
      toast.error(errorMessage(e, t("support.could_not_send")));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-workshops">
      <section className="hero-bg">
        <div className="app-container pt-16 pb-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs tracking-[0.32em] uppercase gold-text" data-testid="workshops-eyebrow">{t("workshops.eyebrow")}</div>
            <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[1.05] max-w-[16ch]" data-testid="workshops-title">{t("workshops.title")}</h1>
            <p className="text-white/75 mt-5 max-w-[52ch] leading-relaxed body-copy">{t("workshops.subtitle")}</p>
          </div>
          <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 max-w-md md:ml-auto">
            <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=85" alt={t("workshops.image_alt")} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
      <Section eyebrow={t("workshops.services_eyebrow")} title={t("workshops.services_title")}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="workshops-services">
          {services.map((s, i) => {
            const Icon = icons[i % icons.length];
            return (
              <div key={i} className="card-surface p-5">
                <Icon className="w-5 h-5 gold-text" />
                <div className="font-display text-xl mt-3">{s}</div>
              </div>
            );
          })}
        </div>
      </Section>
      <Section eyebrow={t("workshops.request_eyebrow")} title={t("workshops.request_title")} subtitle={t("workshops.request_subtitle")}>
        <form onSubmit={submit} className="max-w-xl grid gap-3 card-surface p-6" data-testid="workshop-form">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("support.your_name")} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="workshop-name" required />
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("support.your_email")} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="workshop-email" required />
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("support.message")} rows={5} className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="workshop-msg" required />
          <div>
            <button type="submit" disabled={sending} className="btn-primary inline-flex items-center gap-2" data-testid="workshop-submit"><Send className="w-4 h-4" />{t("workshops.cta")}</button>
          </div>
        </form>
      </Section>
    </div>
  );
}
